(ns dashboard.entity.document.js-dom
  "The principle contract of the dashboard.entity component namespaces are this:
     1. All components will NOT depend on keyword references to subs and events in
        external NSes, but rather receive data as arguments, functions and callbacks.
     2. All internal reliance on the re-frame app DB must be internally contained.
     3. All re-frame app subs and events must NOT be directly referenced by outside
        NSes, but rather via GETTER and SETTER functions - the same is true of internal
        calls - prefer using parameterized functions.
     4. The entity is a UI component that may encapsulate low-level business rules on
        UI controls, but is not responsible for hydrating components with data or entrenched
        references to component namespaces. The UI is customizable, and top-level entities
        should have configurable and interchangable UI elements."
  (:require [clojure.string :as str]))

(defn grandchild->page-number
  "Given an element (expecting a text node inside a span), we reach into the
   grandparent node, and recover the name of the node and parge out the page
   number from the name. That is the name of the page is load-bearing.
   TODO: Improve this hidden dependancy!"
  [elem]
  (some-> elem
          .-parentElement
          .-parentElement
          .-name
          (str/replace #"page-" "")
          js/parseInt))

(defn select-less-than-a-page
  [selection temp-div]
  (let [_ (when (instance? js/Text (.-firstChild temp-div))
                ;; If the user selects inside a single text node, the HTML
                ;; text element is returned, and thus we capture the text
                ;; inside of the selection, update the inner HTML to the
                ;; parent node (span) of the text, and then replace the
                ;; entire text with just the selected text
            (let [text (.-innerText temp-div)]
              (set! (.-innerHTML temp-div) (-> selection
                                               (.getRangeAt 0)
                                               .-startContainer
                                               .-parentElement
                                               .-outerHTML))
              (set! (.-innerText (.-firstChild temp-div)) text)))
            ;; Either way, we NEED to have a span as the first element
        _ (assert (instance? js/HTMLSpanElement (.-firstChild temp-div)))
            ;; but we have to adjust the X offset of the first selected DOM node
            ;; so we capture the beginning of the selection
        start-x (-> selection (.getRangeAt 0) .getClientRects first .-x)
            ;; and the page's bounding rect
        page-rect (-> selection
                      .-anchorNode
                      .-parentElement
                      .-parentElement
                      .getBoundingClientRect)
        page-width (-> page-rect .-width)
            ;; and force the left % of the first selected node to the percentage
            ;; on-screen - without this process, we allow for both one-line selections
            ;; to be properly offset, as well as mutli-line offsets
        _ (aset (.-style (.-firstChild temp-div))
                "left"
                (str (* (/ (- start-x (-> page-rect .-x))
                           page-width)
                        100)
                     "%"))]
    (.-innerHTML temp-div)))

(defn select-multiple-pages
  [selection temp-div]
  (let [;; Across multiple pages, we get a temp div whose first child is the
        ;; position:relative wrapper around the page. That div's first child
        ;; is the text layer on top of the page itself. That div's first child
        ;; is the target span element we want to offset to match the X position
        ;; of selection start.
        _ (assert (instance? js/HTMLDivElement (.-firstChild temp-div)))
        _ (assert (instance? js/HTMLSpanElement (-> temp-div .-firstChild .-firstChild .-firstChild)))
            ;; but we have to adjust the X offset of the first selected DOM node
            ;; so we capture the beginning of the selection
        start-x (-> selection (.getRangeAt 0) .getClientRects first .-x)
            ;; and the page's bounding rect
        page-rect (-> selection
                      .-anchorNode
                      .-parentElement
                      .-parentElement
                      .getBoundingClientRect)
        page-width (-> page-rect .-width)
            ;; and force the left % of the first selected node to the percentage
            ;; on-screen - without this process, we allow for both one-line selections
            ;; to be properly offset, as well as mutli-line offsets
        _ (aset (.-style (-> temp-div .-firstChild .-firstChild .-firstChild))
                "left"
                (str (* (/ (- start-x (-> page-rect .-x))
                           page-width)
                        100)
                     "%"))]
    (.-innerHTML temp-div)))


(defn get-selected-html
  "Given the JS/Window selection object, produce the selected HTML from the
   selection. This function does some complex reasoning on how to deal with
   small, one-line selections of text. The browser will sometimes select only
   text, but in order to save the page coordinates, we need to amend the
   selection to capture the containing DOM node. Either way, we then adjust
   the x-offset of selection of the first selected character and return the
   html content"
  [selection]
  (if (> (.-rangeCount selection) 0)
    (let [range (.getRangeAt selection 0)
          temp-div (js/document.createElement "div")
          _ (.appendChild temp-div (.cloneContents range))
         inner-html (when (.-firstChild temp-div)
                     (if (or (instance? js/Text (.-firstChild temp-div))
                            (instance? js/HTMLSpanElement (.-firstChild temp-div)))
                      (select-less-than-a-page selection temp-div)
                      (select-multiple-pages selection temp-div)))
          _ (.remove temp-div)]
      inner-html)
    nil))


(defn update-css-class
  "Reach into the rendered CSS stylesheet and update the attributes with new
   styles"
  [class-name new-styles]
  ;; Get the first stylesheet
  (let [style-sheet (aget js/document "styleSheets" 0)
        css-rules (.-cssRules style-sheet)]
    ;; Iterate over all the rules in the stylesheet
    (doseq [i (range (.-length css-rules))]
      (let [rule (aget css-rules i)
            selector-text (.-selectorText rule)]
        ;; Check if the rule corresponds to the class to update
        (when (= selector-text (str "." class-name))
          ;; Loop through the new styles and update the rule's style
          (doseq [[property value] new-styles]
            (aset (.-style rule) property value)))))))


(defn extract-spans-from-html
  "Harvests selection HTML from the raw selection, getting rid of the HTML that draws the
   page boundaries between pages - ie. this is used to take a single selection and divide
   it into separate pages."
  [html]
  (let [container (js/document.createElement "div")] ;; Create a container for the raw HTML
    (set! (.-innerHTML container) html) ;; Insert raw HTML into the container

    ;; Remove canvas elements
    (doseq [canvas (array-seq (.getElementsByTagName container "canvas"))]
      (.remove canvas))

    ;; Extract span elements grouped by their div.textLayer containers
    (let [text-layer-divs (array-seq (.getElementsByClassName container "textLayer"))
          contents (->> text-layer-divs
                        (map
                         (fn [div]
                           (mapv
                            (fn [span] (.-outerHTML span)) ;; Return outer HTML of each span
                            (array-seq (.getElementsByTagName div "span")))))
                        (remove empty?)
                        vec)]
      (if (seq contents)
        contents
        [html]))))

(defn remove-all-by-class
  "Delete all elements that have a given class name"
  [class-name]
  (let [elements (.getElementsByClassName js/document class-name)
        elements-array (array-seq elements)]
    (doseq [el elements-array]
      (.. el -parentNode (removeChild el)))))

(defn remove-class-from-all
  "Find all node nodes with a given class name and remove that class from those
   nodes"
  [class-name]
  (let [elements (.getElementsByClassName js/document class-name)
        elements-array (array-seq elements)] ;; Convert HTMLCollection to a seq
    (doseq [el elements-array]
      (.remove (.-classList el) class-name))))

